import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { categories, getMoviesByCategory, movies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'action');
  const [filteredMovies, setFilteredMovies] = useState<Movie[]>([]);
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'views'>('rating');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    let result = getMoviesByCategory(activeSlug);
    if (result.length === 0) {
      result = movies;
    }
    result = [...result].sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'year') return b.year - a.year;
      return b.views - a.views;
    });
    setFilteredMovies(result);
  }, [activeSlug, sortBy]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '电影'} - 68影院`,
    description: `68影院${currentCategory?.name || '电影'}分类，高清免费在线观看${currentCategory?.name || '电影'}大全`,
    url: `https://www.68cinema.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '68影院',
      url: 'https://www.68cinema.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-12 md:pb-16 bg-background-50 min-h-screen">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory?.name || '电影分类' },
              ]}
            />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
              {currentCategory?.name || '电影'} <span className="text-primary-500">大全</span>
            </h1>
            <p className="text-sm text-foreground-500">
              共 {filteredMovies.length} 部{currentCategory?.name}资源，高清免费在线观看
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="w-full lg:w-48 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h3 className="text-sm font-semibold text-foreground-800 mb-3 font-heading">电影类型</h3>
                <div className="flex lg:flex-col flex-wrap gap-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      onClick={() => setActiveSlug(cat.slug)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-all ${
                        activeSlug === cat.slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      <i className={`${cat.icon} text-sm`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
                  {(['rating', 'year', 'views'] as const).map((key) => (
                    <button
                      key={key}
                      onClick={() => setSortBy(key)}
                      className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                        sortBy === key
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-500 hover:text-foreground-800'
                      }`}
                    >
                      {key === 'rating' && '评分最高'}
                      {key === 'year' && '最新上映'}
                      {key === 'views' && '最受欢迎'}
                    </button>
                  ))}
                </div>
              </div>

              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                  {filteredMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-film-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无电影资源</p>
                </div>
              )}

              {filteredMovies.length > 0 && (
                <div className="mt-10 flex items-center justify-center gap-2">
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-sm font-medium">
                    1
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors">
                    2
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors">
                    3
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors">
                    <i className="ri-more-line"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}